/**
 * CookieConsent v3 configuration and initialization
 * Handles consent banner, preferences modal, and category definitions
 */
import * as CookieConsent from 'vanilla-cookieconsent';
import { trackConsentChange } from './visitorTracking';
import { initializeConsentAwarePostHog } from '../lib/posthog';
import {
  COOKIE_CATEGORIES,
  getConsentStatus,
  isConsentGiven,
} from './cookieConsentState';

/**
 * Cookie category definitions for MTS public site
 * 
 * NECESSARY: Required for core functionality (always enabled, user cannot disable)
 *   - mts_visitor_id: Persistent visitor identifier (1 year)
 *   - mts_session_id: Session identifier for visitor context (30 minutes)
 *   - sessionStorage: Impression tracking cache for current session
 * 
 * ANALYTICS: Visitor event tracking and engagement metrics
 *   - Tracks user interactions: listing views, clicks, searches, form submissions
 *   - Used to understand visitor behavior and improve user experience
 *   - Data is stored in MTS backend and not shared with third parties
 * 
 * PREFERENCES: User interface and experience customization (reserved for future use)
 *   - Theme selection, language, interface behavior preferences
 *   - Currently not in use; structured for future enhancements
 */

export { COOKIE_CATEGORIES, getConsentStatus, isConsentGiven };

function synchronizeConsent({ cookie, changedCategories = [] }) {
  // Synchronize capture before sending the necessary consent audit event.
  window.dispatchEvent(new CustomEvent('mts:consent:changed', {
    detail: { categories: cookie.categories, changedCategories, timestamp: new Date().toISOString() },
  }));
  trackConsentChange({ consentState: cookie.categories, changedCategories });
}

export function initializeCookieConsent() {
  initializeConsentAwarePostHog();

  CookieConsent.run({
    categories: {
      necessary: {
        enabled: true,  // always enabled
        readOnly: true, // user cannot disable
      },
      analytics: {
        enabled: false,
      },
      preferences: {
        enabled: false,
      },
    },

    language: {
      default: 'en',
      autoDetect: 'document',
      translations: {
        en: {
          consentModal: {
            title: 'We Use Cookies',
            description:
              'We use cookies to enhance your experience, remember your preferences, and understand how you use our site. You can manage your preferences at any time.',
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject Non-Essential',
            showPreferencesBtn: 'Manage Preferences',
          },
          preferencesModal: {
            title: 'Cookie Preferences',
            acceptAllBtn: 'Accept All',
            acceptNecessaryBtn: 'Reject Non-Essential',
            savePreferencesBtn: 'Save Preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'About Cookies',
                description:
                  'This website uses cookies to provide essential functionality, understand visitor behavior, and improve your experience. We do not sell or share your data with third parties.',
              },
              {
                title: 'Strictly Necessary Cookies',
                description:
                  'These cookies are essential for the website to function properly. They enable session continuity, visitor identification, and core navigation. You cannot disable these cookies.',
                linkedCategory: COOKIE_CATEGORIES.NECESSARY,
              },
              {
                title: 'Analytics & Performance',
                description:
                  'These cookies help us understand how you use our site, which pages you visit, and what you interact with. This information helps us improve your experience and optimize our services.',
                linkedCategory: COOKIE_CATEGORIES.ANALYTICS,
              },
              {
                title: 'Preferences',
                description:
                  'These cookies remember your choices and preferences for a personalized experience, such as language and interface settings.',
                linkedCategory: COOKIE_CATEGORIES.PREFERENCES,
              },
              {
                title: 'More Information',
                description:
                  'For questions about our cookie practices and privacy, please visit our <a href="/privacy-policy" target="_blank">Privacy Policy</a> or <a href="/cookie-policy" target="_blank">Cookie Policy</a>.',
              },
            ],
          },
        },
      },
    },

    // Use localStorage for consent storage (default)
    storage: {
      type: 'localStorage',
    },

    // Cookie configuration
    cookie: {
      name: 'mts_cookie_consent', // consent choice storage
      expiresAfterDays: 365,
    },

    onConsent: synchronizeConsent,
    onChange: synchronizeConsent,

    // Callback before showing consent modal
    onFirstConsent: ({ cookie }) => {
      if (import.meta.env.DEV) {
        console.debug('[CookieConsent] First time user, showing consent modal');
      }
    },
  });
}

/**
 * Show the preferences modal for user to change their cookie preferences
 */
export function showPreferencesModal() {
  CookieConsent.showPreferences();
}

export default {
  initializeCookieConsent,
  isConsentGiven,
  getConsentStatus,
  showPreferencesModal,
  COOKIE_CATEGORIES,
};
